import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ArrowRight, KeyRound, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { setErr("Passwords don't match"); return; }
    if (pw.length < 6) { setErr("Password must be at least 6 chars"); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated!");
      setTimeout(() => navigate("/login"), 2500);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-white/[0.03]">
        <div className="glass-panel rounded-3xl p-10 max-w-md w-full text-center">
          <h1 className="font-display text-2xl font-bold text-white mb-3">Invalid link</h1>
          <p className="text-slate-200 mb-6">This reset link is missing a token.</p>
          <Link to="/forgot-password" className="btn-glow inline-flex">Request a new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-white/[0.03] relative overflow-hidden">
      <div className="orb orb-violet w-[26rem] h-[26rem] -top-32 right-[-5%]" aria-hidden="true" />
      <div className="w-full max-w-md glass-panel rounded-3xl p-10 relative" data-testid="reset-form">
        {done ? (
          <>
            <div className="w-14 h-14 rounded-2xl bg-orange-500/10 border border-orange-400/30 flex items-center justify-center mb-6">
              <CheckCircle2 className="text-orange-400" size={26} />
            </div>
            <h1 className="font-display text-3xl font-bold text-white mb-3">Password updated</h1>
            <p className="text-slate-200">Redirecting you to login…</p>
          </>
        ) : (
          <>
            <div className="w-14 h-14 rounded-2xl bg-orange-500/10 border border-orange-400/30 flex items-center justify-center mb-6">
              <KeyRound className="text-orange-400" size={24} />
            </div>
            <h1 className="font-display text-3xl font-bold text-white mb-3">Set a new password</h1>
            <p className="text-slate-200 text-sm mb-8 font-bn">Choose a new password (min 6 characters).</p>

            {err && <div className="mb-4 border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400 rounded-xl" data-testid="reset-error">{err}</div>}

            <form onSubmit={submit}>
              <label className="block mb-1.5 text-xs uppercase tracking-widest text-slate-200 font-semibold">New password</label>
              <input
                type="password" required value={pw} onChange={(e) => setPw(e.target.value)}
                className="input-dark mb-4"
                data-testid="reset-password"
              />
              <label className="block mb-1.5 text-xs uppercase tracking-widest text-slate-200 font-semibold">Confirm password</label>
              <input
                type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)}
                className="input-dark mb-6"
                data-testid="reset-password-confirm"
              />
              <button type="submit" disabled={busy} className="btn-glow w-full justify-center" data-testid="reset-submit">
                {busy ? "Updating…" : <>Update password <ArrowRight size={14} /></>}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
